import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import {
  AccBooleanValue,
  AccGeoLocationCode,
  AccRehabilitationCode,
  AccSceneCode,
  AccSportsCode,
  IAccInjury,
} from '@principle-theorem/principle-core/interfaces';
import { filterUndefined, ISODateType } from '@principle-theorem/shared';
import { hasRequiredError, setRequiredValidation } from './form-group-helpers';

export type IAccInjuryForm = Omit<
  IAccInjury,
  | 'assistanceRequired'
  | 'accContactProvider'
  | 'admittedToHospital'
  | 'workInjury' // Managed in Employment Form
>;

export type IAccAssistanceForm = Pick<
  IAccInjury,
  'assistanceRequired' | 'accContactProvider' | 'admittedToHospital'
>;

export class AccInjuryFormGroup extends TypedFormGroup<IAccInjuryForm> {
  constructor() {
    super({
      accidentDate: new TypedFormControl<ISODateType>(
        undefined,
        Validators.required
      ),
      accidentLocationCode: new TypedFormControl<AccGeoLocationCode>(
        undefined,
        Validators.required
      ),
      accidentSceneCode: new TypedFormControl<AccSceneCode>(
        undefined,
        Validators.required
      ),
      causeOfAccident: new TypedFormControl<string>('', Validators.required),
      involvesVehicle: new TypedFormControl<AccBooleanValue>(
        undefined,
        Validators.required
      ),
      medicalTreatmentInjury: new TypedFormControl<AccBooleanValue>(undefined),

      sportingInjury: new TypedFormControl<AccBooleanValue>(
        undefined,
        Validators.required
      ),
      sportNameCode: new TypedFormControl<AccSportsCode>(
        undefined,
        Validators.required
      ),
      gradualProcessInjury: new TypedFormControl<AccBooleanValue>(undefined),
      injuryComments: new TypedFormControl<string>(''),
    });

    this.controls.sportingInjury.valueChanges
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((value) =>
        setRequiredValidation(value, this.controls.sportNameCode)
      );
  }

  get accidentDateRequiredError(): boolean {
    return hasRequiredError(this.controls.accidentDate);
  }

  get accidentLocationCodeRequiredError(): boolean {
    return hasRequiredError(this.controls.accidentLocationCode);
  }

  get accidentSceneCodeRequiredError(): boolean {
    return hasRequiredError(this.controls.accidentSceneCode);
  }

  get causeOfAccidentRequiredError(): boolean {
    return hasRequiredError(this.controls.causeOfAccident);
  }

  get involvesVehicleRequiredError(): boolean {
    return hasRequiredError(this.controls.involvesVehicle);
  }

  get sportingInjuryRequiredError(): boolean {
    return hasRequiredError(this.controls.sportingInjury);
  }

  get sportNameCodeRequiredError(): boolean {
    return hasRequiredError(this.controls.sportNameCode);
  }

  get displaySportNameCode(): boolean {
    return this.controls.sportingInjury.value === AccBooleanValue.True;
  }
}

export class AccAssistanceFormGroup extends TypedFormGroup<IAccAssistanceForm> {
  constructor() {
    super({
      admittedToHospital: new TypedFormControl<AccBooleanValue>(undefined), // Only DHBs can set this to true
      assistanceRequired: new TypedFormControl<AccBooleanValue>(undefined),
      accContactProvider: new TypedFormControl<AccRehabilitationCode>(
        undefined
      ),
    });
  }
}
